import 'tinymce/icons/default';
import 'tinymce/themes/silver';

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, ValidationErrors } from '@angular/forms';
import { Validator } from '@vendasta/shared';
import { Subscription } from 'rxjs';

declare let tinymce: any;

// If we want to turn this into a proper input
// https://blog.angular-university.io/angular-custom-form-controls/

const tinyMCEHyperlinkTextColour = '#057EC1';

@Component({
  selector: 'email-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
})
export class RichTextEditorComponent implements AfterViewInit, OnDestroy {
  @HostBinding('class') class = 'email-rich-text-editor';

  @ViewChild('richEditorDiv') richEditorDiv: ElementRef<HTMLElement>;

  @Input() disabled: boolean;
  @Input() menubar: any = false;
  @Input() menu: any = false;
  @Input() toolbar: string[] = [
    'bold italic link unlink | alignleft aligncenter alignright | bullist numlist | code removeformat',
  ];
  @Input() plugins = 'lists link code image';
  @Input() width: number;
  @Input() height: string | number = '100%';
  // This is for uploading images
  @Input() showUploadTab = false;
  @Input() invalidElements: string;

  @Input() set value(value: string) {
    this._value = value;
  }
  @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() statusChange: EventEmitter<ValidationErrors | null> = new EventEmitter<ValidationErrors | null>();

  _value: string;
  editorIsReady = false;

  private editor: any;
  private subscriptions: Subscription[] = [];

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.valueChange.subscribe((value) => {
        if (value !== '') {
          const isValid = Validator.containsValidDynamicContent({ value: value } as UntypedFormControl);
          this.statusChange.emit(isValid);
        }
      }),
    );
    // using setTimeout as a hack to make sure everything is
    // ready before loading
    window.setTimeout(() => {
      this.setupEditor();
    }, 0);
  }

  ngOnDestroy(): void {
    tinymce.remove(this.editor);
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  onEditorChange(): void {
    if (!this.editor) {
      return;
    }
    const newContent = this.editor.getContent();
    if (newContent === this._value) {
      return;
    } else {
      this.valueChange.emit(newContent);
      this._value = newContent;
    }
  }

  public reloadEditor(): void {
    console.log('reloadEditor');
    tinymce.remove(this.editor);
    window.setTimeout(() => {
      this.setupEditor();
    }, 0);
  }

  insertAtCursor(text: string): void {
    this.editor.insertContent(text);
  }

  focus(): void {
    this.richEditorDiv.nativeElement.focus();
  }

  setupEditor(): void {
    tinymce.baseURL = 'https://storage.googleapis.com/developers-vendasta-client/assets';
    tinymce.init({
      browser_spellcheck: true,
      content_style: `a {color: ${tinyMCEHyperlinkTextColour}`,
      target: this.richEditorDiv.nativeElement,
      // selector: '#' + this.elementId,
      readonly: this.disabled,
      width: this.width,
      height: this.height,
      menubar: this.menubar,
      menu: this.menu,
      toolbar: this.toolbar,
      plugins: this.plugins,
      convert_urls: false,
      allow_conditional_comments: true,
      valid_children: '+body[style]',
      invalid_elements: this.invalidElements || null,
      branding: false,
      setup: (editor) => {
        this.editor = editor;
        editor.on('init', () => {
          this.editor.setContent(this._value);
        });
        // prevents this.editor from being undefined on events
        const updateHandler = () => {
          this.onEditorChange();
        };
        editor.on('keyup', updateHandler);
        editor.on('change', updateHandler);
        editor.on('NodeChange', updateHandler);
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      images_upload_handler: function (blobInfo: any, success: any, failure: any): void {
        //
      },
      images_upload_url: this.showUploadTab ? 'any-non-empty-value' : '',
      init_instance_callback: () => {
        this.editorIsReady = true;
      },
    });
  }
}
